// Use the "/max" metadata bundle: its isValid() applies the full national-number patterns,
// matching the backend's Google libphonenumber isValidNumber(). The bare "libphonenumber-js"
// entry ships the "min" bundle, where isValid() only checks length and would accept numbers the
// backend rejects (see Issue-41587).
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js/max";

// Default region used when parsing a number that is not already in +E.164 form — mirrors the
// backend PhoneNumberValidationHelper, which parses with region "DE".
const DEFAULT_COUNTRY = "DE";

export const getCountryList = () => {
    try {
        const countries = getCountries();
        const dn =
            typeof Intl !== "undefined" && Intl.DisplayNames ? new Intl.DisplayNames(["de"], { type: "region" }) : null;

        const list = countries.map((iso) => {
            const code = "+" + getCountryCallingCode(iso);
            const name = (dn && dn.of(iso)) || iso;
            return { iso, code, name };
        });

        // Sort alphabetically by country name
        list.sort((a, b) => a.name.localeCompare(b.name));

        // Move Germany (+49) to the top
        const deIndex = list.findIndex((c) => c.code === "+49" || c.iso === "DE");
        if (deIndex > 0) {
            const [de] = list.splice(deIndex, 1);
            list.unshift(de);
        }

        return list;
    } catch (e) {
        // Fallback to Germany only if there's an error
        return [{ iso: "DE", code: "+49", name: "Germany" }];
    }
};

/**
 * Composes the full international phone number from the selected country code and the
 * national number the user typed into the local field, normalizing common mistakes:
 * - a full international number pasted into the local field (leading "+") is taken as-is,
 *   so the selected country code is NOT prepended a second time (avoids "+49 +44…" → "+4944…")
 * - a "00" international prefix typed into the local field is converted to "+"
 * - otherwise the selected country code is prepended to the typed digits
 *
 * Note: we deliberately do NOT strip a bare leading country code typed WITHOUT a "+" (e.g.
 * "49…" with +49 selected), because valid national numbers can legitimately start with the
 * same digits (e.g. the German area code 0491 → national "491…"). Such input is rejected by
 * validation and the user is asked to enter the number without the country code instead.
 *
 * @returns the E.164 number when parseable, otherwise the best-effort concatenation,
 *          or null when no national number was entered.
 */
export const composeFullPhoneNumber = (countryCode, local) => {
    if (!local || local.trim() === "") {
        return null;
    }

    const trimmed = local.trim();

    // User pasted a full international number (leading "+") into the local field.
    if (trimmed.startsWith("+")) {
        const internationalCandidate = "+" + trimmed.slice(1).replace(/\D/g, "");
        const parsedInternational = parsePhoneNumberFromString(internationalCandidate, DEFAULT_COUNTRY);
        return parsedInternational ? parsedInternational.number : internationalCandidate;
    }

    const digits = trimmed.replace(/\D/g, "");
    if (digits === "") {
        return null;
    }

    // User typed the international prefix "00<country>…" into the local field.
    const candidate = digits.startsWith("00") ? "+" + digits.slice(2) : `${countryCode}${digits}`;

    const parsed = parsePhoneNumberFromString(candidate, DEFAULT_COUNTRY);
    return parsed ? parsed.number : candidate;
};

/**
 * Parses a phone number as stored in the backend, which may be a clean +E.164 value or a legacy
 * value saved before Issue-41587 without a leading "+" - either already containing the country
 * calling code (e.g. "4917612345678") or in national format (e.g. "017612345678").
 *
 * Tries, in order: as-is (when it already has a "+"), with a "+" prepended (covers a missing "+"
 * in front of an already-embedded calling code, without which region-based parsing would misread
 * those digits as part of the national number - see Issue-42089), then as a DEFAULT_COUNTRY
 * national number. Returns null when none of these produce a valid number.
 *
 * The "+" prepend is only attempted when the value already starts with DEFAULT_COUNTRY's own
 * calling code ("49") - German mobile numbers stored without a country code start with "1", which
 * collides with NANP's "+1" and would otherwise be silently misparsed as a US/Canada number.
 */
export const parseStoredPhoneNumber = (fullPhoneNumber) => {
    if (!fullPhoneNumber || fullPhoneNumber.trim() === "") {
        return null;
    }

    if (fullPhoneNumber.startsWith("+")) {
        const parsed = parsePhoneNumberFromString(fullPhoneNumber);
        return parsed && parsed.isValid() ? parsed : null;
    }

    if (fullPhoneNumber.startsWith(getCountryCallingCode(DEFAULT_COUNTRY))) {
        const withCallingCode = parsePhoneNumberFromString("+" + fullPhoneNumber);
        if (withCallingCode && withCallingCode.isValid()) {
            return withCallingCode;
        }
    }

    const national = parsePhoneNumberFromString(fullPhoneNumber, DEFAULT_COUNTRY);
    return national && national.isValid() ? national : null;
};

/**
 * Whether the national number entered for the selected country forms a valid phone number.
 * An empty input is treated as valid here — required-ness is enforced separately.
 */
export const isPhoneNumberValid = (countryCode, local) => {
    if (!local || local.trim() === "") {
        return true;
    }

    return isFullPhoneNumberValid(composeFullPhoneNumber(countryCode, local));
};

/**
 * Whether an already-composed full phone number (e.g. E.164) is valid.
 * An empty/blank value is treated as valid — required-ness is enforced separately.
 */
export const isFullPhoneNumberValid = (fullPhoneNumber) => {
    if (!fullPhoneNumber || fullPhoneNumber.trim() === "") {
        return true;
    }

    const parsed = parsePhoneNumberFromString(fullPhoneNumber, DEFAULT_COUNTRY);
    return Boolean(parsed && parsed.isValid());
};

/**
 * Formats an E.164 phone number for read-only display (e.g. "+49 151 23456789"). Falls back to
 * the raw value when it cannot be parsed and returns an empty string for blank input. Use this
 * for table cells and detail views instead of mounting the editable input.
 */
export const formatPhoneNumber = (fullPhoneNumber) => {
    if (!fullPhoneNumber || fullPhoneNumber.trim() === "") {
        return "";
    }

    const parsed = parsePhoneNumberFromString(fullPhoneNumber, DEFAULT_COUNTRY);
    return parsed ? parsed.formatInternational() : fullPhoneNumber;
};
